import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getBudgetStatus, getJob, getQueueStatus, listReports } from "@/lib/api";

const POLL_INTERVAL = 4000;
const JOB_KEY = "crash_job_id";

/**
 * Owns the dashboard's data-fetching lifecycle:
 *   • job summary + report list polled every 4s while a job exists
 *   • budget kill-switch state
 *   • derived stats (memoised) used by the UI
 *
 * Errors during polling are swallowed (logged to console) so a transient
 * 502 from the ingress during heavy LLM work can never trigger React's
 * dev-mode error overlay.
 */
export function useJobPolling({ injuryFilter, search }) {
  const [jobId, setJobIdState] = useState(() => localStorage.getItem(JOB_KEY) || null);
  const [job, setJob] = useState(null);
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [budget, setBudget] = useState({ tripped: false, message: "" });
  const [queueStatus, setQueueStatus] = useState({ active: false, active_job_id: null, queued_count: 0 });

  const setJobId = useCallback((next) => {
    setJobIdState(next);
    if (next) localStorage.setItem(JOB_KEY, next);
    else localStorage.removeItem(JOB_KEY);
  }, []);

  const clearLocal = useCallback(() => {
    localStorage.removeItem(JOB_KEY);
    setJobIdState(null);
    setJob(null);
    setReports([]);
  }, []);

  const refreshJob = useCallback(async (id = jobId) => {
    if (!id) return null;
    try {
      const j = await getJob(id);
      setJob(j);
      return j;
    } catch (e) {
      if (e?.response?.status === 404) clearLocal();
      else console.warn("refreshJob failed:", e?.response?.status || e.message);
      return null;
    }
  }, [jobId, clearLocal]);

  const refreshReports = useCallback(async (id = jobId) => {
    if (!id) return;
    setLoadingReports(true);
    try {
      const params = { search };
      if (injuryFilter === "04") {
        params.injury_code = "04";
        params.transported = true;
      } else if (injuryFilter === "04nt") {
        params.injury_code = "04";
        params.transported = false;
      } else if (injuryFilter) {
        params.injury_code = injuryFilter;
      }
      const r = await listReports(id, params);
      setReports(r);
    } catch (e) {
      if (e?.response?.status === 404) clearLocal();
      else console.warn("refreshReports failed:", e?.response?.status || e.message);
    } finally {
      setLoadingReports(false);
    }
  }, [jobId, injuryFilter, search, clearLocal]);

  const refreshBudget = useCallback(async () => {
    try {
      const b = await getBudgetStatus();
      setBudget(b);
    } catch (e) {
      console.warn("refreshBudget failed", e);
    }
  }, []);

  const lastSigRef = useRef("");

  // Global queue snapshot — polled even with NO local job so a fresh staff
  // member sees "a report is already running" before uploading. Tiny payload.
  useEffect(() => {
    const tick = () => getQueueStatus().then(setQueueStatus).catch(() => {});
    tick();
    const t = setInterval(tick, POLL_INTERVAL * 2);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!jobId) return;
    lastSigRef.current = ""; // new job / filter / search → always refetch reports once
    const tick = async () => {
      refreshBudget().catch(() => {});
      const j = await refreshJob(jobId).catch(() => null);
      // Only refetch the (heavy) reports list when the summary actually
      // changed — idle dashboards stop hammering the server.
      const sig = j
        ? JSON.stringify([j.processed, j.failed, j.qualifying, j.total_files, j.llm_calls, j.counts])
        : "";
      if (!j || sig !== lastSigRef.current) {
        lastSigRef.current = sig;
        refreshReports(jobId).catch(() => {});
      }
    };
    tick();
    const t = setInterval(tick, POLL_INTERVAL);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, injuryFilter, search]);

  const stats = useMemo(() => {
    const c = job?.counts || { "01": 0, "02": 0, "03": 0, "04": 0, "04nt": 0, pedestrians: 0 };
    return {
      total: job?.total_files || 0,
      qualifying: job?.qualifying || 0,
      failed: job?.failed || 0,
      processed: job?.processed || 0,
      fatal: c["01"] || 0,
      serious: c["02"] || 0,
      minor: c["03"] || 0,
      possible: c["04"] || 0,
      possibleNoAmb: c["04nt"] || 0,
      pedestrians: c["pedestrians"] || 0,
      // Files extracted but where no person had a qualifying injury —
      // intentionally hidden from the main table but the user needs to
      // see the count so the math adds up vs the zip they uploaded.
      skipped: Math.max(0, (job?.processed || 0) - (job?.qualifying || 0) - (job?.failed || 0)),
      llmCalls: job?.llm_calls || 0,
      estCostUsd: job?.est_cost_usd || 0,
    };
  }, [job]);

  return {
    jobId,
    setJobId,
    clearLocal,
    job,
    reports,
    loadingReports,
    budget,
    setBudget,
    queueStatus,
    stats,
    refreshJob,
    refreshReports,
    refreshBudget,
    POLL_INTERVAL,
  };
}
